import { useEffect, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { HeatmapTable } from "@similarweb/pro-logic-components";
import type { IDraggableItem } from "@similarweb/ui-components";
import { Stack } from "@similarweb/ui-components/foundations";

import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { translate } from "app/filters/i18nFilter";
import { ALL_TOPICS_NAME } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import {
    getBaseWidgetRequestParams,
    getWidgetNoDataProps,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { ILLMReportWidget } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { ReportStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { requestParams } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";

import { BrandsQueryBar } from "../components/brands-query-bar/BrandsQueryBar";
import { useBrandComparisonData } from "../hooks/useBrandComparisonData";

import { formatCellValue, transformToHeatmapRows } from "./brandComparisonUtils";
import { getColumnConfig } from "./config";

export const BrandComparisonComponent = ({
    campaign,
    campaignConfig,
    reportStatus,
    reportDuration,
}: ILLMReportWidget) => {
    const {
        params: { topic: queryTopic },
    } = useCurrentStateAndParams();

    const selectedTopic = (queryTopic as string) ?? ALL_TOPICS_NAME;
    const [selectedCompetitorBrands, setSelectedCompetitorBrands] = useState<string[]>([]);
    const canLoadData = reportStatus === ReportStatus.Ready && !!campaign;
    const campaignBrand = campaign?.brands?.[0]?.toLowerCase();

    const apiParams = useMemo(
        () => getBaseWidgetRequestParams(campaign, campaignConfig, reportDuration),
        [campaign, campaignConfig, reportDuration],
    );

    const { data, isLoading, isError } = useBrandComparisonData(apiParams, { enabled: canLoadData });

    const excelUrl =
        canLoadData && campaign?.id
            ? `/api/aiTracker/reports/sentimentAnalysis/BrandComparison/Excel?${requestParams(apiParams)}`
            : "";

    const onExcelClick = (): void => {
        allTrackers.dispatchTrackEvent("Download", "submit-ok", "Table/Sentiment Score Comparison/Excel");
    };

    const filteredData = useMemo(
        () =>
            selectedTopic === ALL_TOPICS_NAME
                ? data?.data
                : data?.data?.filter((row) => row.topic?.toLowerCase() === selectedTopic.toLowerCase()),
        [data, selectedTopic],
    );

    const hasNoTopics = !isLoading && filteredData?.length === 0;
    const shouldShowNoData = isError || hasNoTopics || reportStatus === ReportStatus.Error;
    const shouldShowLoading = (isLoading || !campaign) && reportStatus !== ReportStatus.Error;

    useEffect(() => {
        const brandsList = data?.topCompetitorBrands?.length
            ? data.topCompetitorBrands
            : data?.brands?.length
              ? data.brands //fallback to general competitors
              : [];

        if (brandsList?.length) {
            // Filter out campaign brand to avoid duplication in comparison
            const competitorBrands = brandsList.filter((brand) => brand.toLowerCase() !== campaignBrand);
            setSelectedCompetitorBrands(competitorBrands.slice(0, 4));
        } else {
            // Reset competitor brands when no data is available
            setSelectedCompetitorBrands([]);
        }
    }, [data, campaignBrand]);

    const tableDataAndColumnConfig = useMemo(() => {
        if (!filteredData || !campaignBrand) {
            return {
                columnConfig: [],
                data: [],
            };
        }
        const columnConfig = getColumnConfig(selectedCompetitorBrands);
        const flattened = transformToHeatmapRows(filteredData, selectedCompetitorBrands);
        return {
            columnConfig,
            data: flattened,
        };
    }, [campaignBrand, filteredData, selectedCompetitorBrands]);

    const onUpdateBrand = (currentBrand: string, brand: string) => {
        const updatedBrands = selectedCompetitorBrands.map((b) => (b !== currentBrand ? b : brand));
        setSelectedCompetitorBrands(updatedBrands);
    };

    const onAddBrand = (brand: string) => {
        setSelectedCompetitorBrands((prev) => [...prev, brand]);
        allTrackers.dispatchTrackEvent("Button", "click", `Sentiment Score/add/${brand}`);
    };

    const onRemoveBrand = (brand: string) => {
        setSelectedCompetitorBrands((prev) => prev.filter((b) => b !== brand));
        allTrackers.dispatchTrackEvent("Button", "click", `Sentiment Score/remove/${brand}`);
    };

    const onDragEnd = (items: IDraggableItem[]) => {
        setSelectedCompetitorBrands(items.map((item: IDraggableItem) => item.id));
    };

    return (
        <WidgetContainer
            {...getWidgetNoDataProps(shouldShowNoData, isError)}
            data-automation="brand-comparison-widget"
            title={translate("llm.sentiment_report.brand_comparison.title")}
            tooltip={translate("llm.sentiment_report.brand_comparison.tooltip")}
            isLoading={shouldShowLoading}
            loaderWidth={"100%"}
            loaderHeight={300}
            withBorder
            excelUrl={excelUrl}
            onExcelClick={onExcelClick}
        >
            <Stack
                gap="32px"
                padding="16px 24px"
            >
                <BrandsQueryBar
                    campaignBrand={campaignBrand}
                    competitorBrands={data?.brands ?? []}
                    onUpdateBrand={onUpdateBrand}
                    onAddBrand={onAddBrand}
                    onRemoveBrand={onRemoveBrand}
                    selectedCompetitorBrands={selectedCompetitorBrands}
                    onDragEnd={onDragEnd}
                />
                <HeatmapTable
                    data-automation="brand-comparison-table"
                    key={selectedCompetitorBrands.join()}
                    theme="white"
                    size="large"
                    includeZeroInHeatmap
                    formatter={formatCellValue}
                    bordersConfig={{
                        body: false,
                        header: false,
                    }}
                    sticky={false}
                    columnConfig={tableDataAndColumnConfig.columnConfig}
                    data={tableDataAndColumnConfig.data}
                    isLoading={shouldShowLoading}
                    loaderRowCount={5}
                    noData={{
                        title: translate("common.noresults"),
                        subtitle: translate("common.noresults.subtitle"),
                    }}
                />
            </Stack>
        </WidgetContainer>
    );
};
